import { useState, useEffect, useCallback } from "react";

type QueryState<T> = {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
};

const cache = new Map<string, any>();

// Simple implementation similar to Tanstack Query. It might be
// a good idea to replace it with TanstackQuery if complex usage is needed
export function useQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  options?: { staleTime?: number; enabled?: boolean }
): QueryState<T> {
  const { enabled = true, staleTime = 0 } = options || {};
  const [state, setState] = useState<QueryState<T>>({
    data: cache.get(key) ?? null,
    error: null,
    isLoading: enabled && !cache.has(key),
  });

  const fetchData = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const data = await fetcher();
      cache.set(key, data);
      setState({ data, error: null, isLoading: false });
    } catch (error) {
      setState({ data: null, error: error as Error, isLoading: false });
    }
  }, [key, fetcher]);

  useEffect(() => {
    if (!enabled) return;

    if (!cache.has(key) || staleTime === 0) {
      fetchData();
    }
  }, [key, fetchData, staleTime, enabled]);

  return state;
}
